import { Extension, Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Slice } from 'prosemirror-model';
import isEmpty from 'lodash/isEmpty';
import { Fragment, Node } from '@tiptap/pm/model';

import { cleanMark as cleanHighlight } from './marks/highlight';
import { cleanMark as cleanTextStyle } from './marks/textStyle';
import { cleanMark as cleanFontFamily } from './marks/fontFamily';

/**
 * Walk all the nodes in the fragment, return new fragment
 *
 * Prose has slightly odd way at handling empty <p> with hardbreaks, this filter fixes
 * pasting from external source (google, libre) where empty <p> comes as <p><br></p> and
 * then editor renders that as <p><br><br></p>
 *
 * see https://github.com/ProseMirror/prosemirror/issues/1241#issuecomment-1035193462
 *
 * So we already fix this in viewer so that viewer also does double br for last hard break
 * this gives us consistency between editor and viewer. However we then need this to clean
 * pastes for incoming html where we want to preserve that layout not the editor quirk.
 *
 * Logic is to simply find "empty" p with only a hardbreak inside and remove said hardbreak.
 * This might miss some trailing hardbreak but I think it will be good enough.
 */
function filterPastedFragment(fragment: Fragment, editor: Editor): Fragment {
  const newNodes: Node[] = [];
  fragment.forEach(node => {
    markTrailingHardbreaks(node);
    if (shouldFilterHardbreak(node)) {
      return;
    }
    return newNodes.push(filterMarksFromNode(node, editor));
  });
  return Fragment.fromArray(newNodes);
}

/**
 * Mark the trailing breaks for filtering, we will be in the parent when this is called
 * and the next level will include the target break
 */
function markTrailingHardbreaks(node: Node) {
  if (node.type.name === 'paragraph') {
    const first = node.firstChild;
    const last = node.lastChild;
    if (first?.type.name === 'hardBreak' && first === last) {
      (first as any).__filter = true;
    }
  }
}

/**
 * Check if this should be filtered
 */
function shouldFilterHardbreak(node: Node) {
  if (node.type.name === 'hardBreak' && (node as any).__filter === true) {
    return true;
  }
  return false;
}

function filterMarksFromNode(node: Node, editor): Node {
  if (node.marks.length > 0) {
    const cleanMarks = node.marks.map(m => {
      const attrs: Record<string, any> = {
        ...cleanHighlight(m),
        ...cleanTextStyle(m),
        ...cleanFontFamily(m),
      };
      if (!isEmpty(attrs)) {
        return editor.schema.marks[m.type.name].create(attrs);
      }
      return m;
    });
    // mark returns new node with new marks and then copy lets us filter the children
    return node
      .mark(cleanMarks)
      .copy(filterPastedFragment(node.content, editor));
  }

  // No filtering done but we still need new copy so we can process children
  return node.copy(filterPastedFragment(node.content, editor));
}

const Paste = Extension.create({
  name: 'da-paste-cleaner',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('da-paste-cleaner'),
        props: {
          // Replace double spacing with empty line, this helps maintain spacing as
          // our paragraphs have no margins for historical reasons.
          // Explanation of the regex:
          //  - (?<!\n): Negative lookbehind to ensure there is no preceding newline, i.e., we want to match only exactly two newlines, not more.
          // - \n\n: Matches exactly two consecutive newline characters.
          // - (?!\n): Negative lookahead to ensure that there is no following newline, ensuring exactly two newlines are matched.
          transformPastedText(text, plain, view) {
            return text.replace(/(?<!\n)\n\n(?!\n)/gm, '\n\n \n');
          },
          // Move h1 -> h2 before we parse the html
          transformPastedHTML: html => {
            return html.replace(/<h1[^>]*>(.*?)<\/h1>/g, '<h2>$1</h2>');
          },
          transformPasted: (slice, view) => {
            return new Slice(
              filterPastedFragment(slice.content, this.editor),
              slice.openStart,
              slice.openEnd
            );
          },
        },
      }),
    ];
  },
});

export { Paste };
